import React from "react"
import { FormattedMessage, useIntl } from "gatsby-plugin-intl"
import { Tooltip } from "react-tooltip"
import { useLocation } from "@reach/router"
import { LazyImage, TruncatedText, Button, Money } from "@tmu/components/common"
import { CardStats } from "../CardStats"
import { getAllScreenTypes } from "@tmu/utils/mediaQueries"

import {
  StyledCardTitle,
  StyledCard,
  StyledCardInfo,
  StyledCardSubtitle,
  StyledImageBox,
  StyledPACMultiplier,
  StyledDoublePacsTooltip,
  StyledCharityStatsContainer,
  StyledChooseButtonContainer,
} from "./index.styles"

const { CLOUDFLARE_IMAGE_URL } = process.env

const CharityCard = ({
  title,
  subtitle,
  description,
  totalDonors,
  totalSupport,
  discount,
  inSupportOfTitle,
  inSupportOfLogo,
  fiscalCode,
  size,
  cardLink,
  goalAmount,
  fundedAmount,
  type,
  partnerName,
  partnerLogo,
  hasLogo,
  taxId,
  pacMultiplier,
  id,
  ...rest
}) => {
  const { formatMessage } = useIntl()
  const pacMultiplierNumber = pacMultiplier?.split(".")[0]
  const { isTablet, isDesktop, isWide } = getAllScreenTypes()
  const { pathname } = useLocation()

  const doublePacsTipText = formatMessage(
    {
      id: "doublePacs::listing::cardTooltip",
      defaultMessage:
        "You will get {pacMultiplierNumber} PACs for every EUR donated",
    },
    {
      pacMultiplierNumber: pacMultiplierNumber,
    }
  )

  const defaultSubtitle = (
    <>
      {isTablet ? (
        <>
          <FormattedMessage
            id="typehead::partners::fiscalCode"
            defaultMessage="Fiscal Code"
          />
          : {taxId}
        </>
      ) : (
        <FormattedMessage
          id="dashboard::charityType::charity"
          defaultMessage="Charity"
        />
      )}
    </>
  )
  const PacMultiplier = () => {
    return Number(pacMultiplier) > 1 ? (
      <StyledPACMultiplier>
        <img
          className="pacs-pacs-image"
          src={CLOUDFLARE_IMAGE_URL + "/static/assets/images/white_pac.svg"}
          alt={`pacs-pacs-banner`}
          width={28}
          height={23}
        />
        {` X `}
        <Money value={pacMultiplier} currency={false} />
      </StyledPACMultiplier>
    ) : null
  }

  const imageSize = {
    width: "auto",
    height: isWide ? 196 : isDesktop ? 122 : 108,
  }

  const handleChoose = (e) => {
    if (typeof rest?.chooseAction === "function") {
      rest?.chooseAction()
    }
    e.stopPropagation()
  }

  return (
    <StyledCard
      size={size}
      {...rest}
      onClick={handleChoose}
      data-testid={`charityCardWithCTA${id}`}>
      <StyledImageBox hasLogo size={size}>
        <LazyImage
          src={partnerLogo}
          {...imageSize}
          altName={`card-image`}
          fit="contain"
        />
        {pacMultiplier && (
          <StyledDoublePacsTooltip
            data-tooltip-id={`charityCard${id}`}
            data-tooltip-content={doublePacsTipText}>
            <PacMultiplier />
            <Tooltip
              arrowColor="transparent"
              effect="solid"
              place="bottom"
              offset={{
                bottom: isWide ? 40 : 20,
                right: isWide ? 59 : 10,
              }}
              type="info"
              textColor="black"
              backgroundColor="white"
              id={`charityCard${id}`}
              className={`charityCard ${
                pathname.includes("campaigns") ||
                pathname.includes("user-space")
                  ? "charityCardListing"
                  : ""
              }`}
              delayShow={500}
            />
          </StyledDoublePacsTooltip>
        )}
      </StyledImageBox>
      <StyledCardInfo size={size}>
        <StyledCardTitle>
          <TruncatedText numLines={2}>{title}</TruncatedText>
        </StyledCardTitle>
        <StyledCardSubtitle className="caption">
          {subtitle || defaultSubtitle}
        </StyledCardSubtitle>
      </StyledCardInfo>

      <StyledCharityStatsContainer>
        <CardStats
          totalDonors={totalDonors}
          totalSupport={totalSupport}
          size={size}
        />
        {isTablet ? (
          <StyledChooseButtonContainer>
            <Button
              data-testid="btn-discard"
              color="transparent"
              className="button-margin"
              type="button"
              onClick={handleChoose}>
              <FormattedMessage
                id="card::charity::choose"
                defaultMessage="Choose"
              />
            </Button>
          </StyledChooseButtonContainer>
        ) : (
          <span className="caption">
            <FormattedMessage
              id="typehead::partners::fiscalCode"
              defaultMessage="Fiscal Code"
            />
            : {taxId}
          </span>
        )}
      </StyledCharityStatsContainer>
    </StyledCard>
  )
}

export default CharityCard
